import {CategorySearchService} from '../services/category-search-service';

interface SearchResultUsedEvent {
    score: number;
    keyword: string;
    label: string;
    position: number;
    search: string;
}

const root = document.getElementById('category-search-root')!;
const input = root.querySelector<HTMLInputElement>('#categorySearchInput')!;
const dropdownMenu = root.querySelector<HTMLDivElement>('#dropdownMenu')!;
const backdrop = root.querySelector<HTMLDivElement>('#backdrop')!;
const clearIcon = root.querySelector<HTMLSpanElement>('#clearIcon')!;
const searchIcon = root.querySelector<HTMLSpanElement>('#searchIcon')!;
const loadingIcon = root.querySelector<HTMLSpanElement>('#loadingIcon')!;
const startProjectBtn = root.querySelector<HTMLButtonElement>('#startProjectBtn')!;
const locale = root.dataset.locale || 'en';

const service = new CategorySearchService();
let results: SearchResult[] = [];
let selectedIndex: number | null = null;
let selectedResult: SearchResult | null = null;
let debounceTimeout: number;

const MIN_SEARCH_CHARS = 2;

function clearSearch(): void {
    input.value = '';
    selectedResult = null;
    results = [];
    selectedIndex = null;
    dropdownMenu.innerHTML = '';
    dropdownMenu.style.display = 'none';
    updateIcons();
    input.focus();
}

function updateIcons(): void {
    clearIcon.style.display = input.value.length > 0 && selectedResult === null ? 'inline' : 'none';
    searchIcon.style.display = input.value.length === 0 && selectedResult === null ? 'inline' : 'none';
    loadingIcon.style.display = selectedResult !== null ? 'inline' : 'none';
}

function renderNoResults(): void {
    const container = document.createElement('div');
    container.className = 'dropdown-menu d-block sd-py-0_5 sd-my-0_5 no-results';

    const link = document.createElement('a');
    link.href = '#';
    link.className = 'dropdown-item sd-p-0_5 sd-px-0_5 small';
    link.style.whiteSpace = 'normal';
    link.textContent = `No results for "${String(input.value)}"`;

    link.addEventListener('click', (e) => {
        e.preventDefault();
        clearSearch();
    });

    container.appendChild(link);
    dropdownMenu.appendChild(container);
    dropdownMenu.style.display = 'block';
}

function renderResults(): void {
    dropdownMenu.innerHTML = '';

    if (results.length === 0) {
        renderNoResults();
        return;
    }

    const container = document.createElement('div');
    container.className = 'dropdown-menu d-block sd-py-0_5 sd-my-0_5';

    results.forEach((result, index) => {
        const link = document.createElement('a');
        link.href = '#';
        link.className = 'dropdown-item sd-p-0_5 sd-px-0_5';
        link.textContent = String(result.item.label ?? '');

        if (index === selectedIndex) {
            link.classList.add('first');
        }

        link.addEventListener('click', (e) => {
            e.preventDefault();
            handleResultClick(result, index);
        });

        container.appendChild(link);
    });

    dropdownMenu.appendChild(container);
    dropdownMenu.style.display = 'block';
}

function handleResultClick(result: SearchResult, index: number): void {
    input.value = result.item.label ?? '';
    selectedResult = result;

    backdrop.style.display = 'none';
    dropdownMenu.innerHTML = '';
    dropdownMenu.style.display = 'none';
    updateIcons();

    dispatchSearchResultUsed(result, index);

    const category = result.item.category;
    const type = result.item.type ?? '';
    let target = `/Offertes/${String(result.item.category)}/form`;

    if (type) {
        target += `?type=${String(type)}&category=${String(category)}`;
    }

    const refMatch = /ref=([^&]+)/.exec(location.search);
    if (refMatch) {
        target += target.includes('?') ? `&ref=${refMatch[1]}` : `?ref=${refMatch[1]}`;
    }

    location.assign(target);
}

function handleInput(): void {
    const value = input.value.trim();
    selectedResult = null;
    updateIcons();

    if (debounceTimeout) {
        clearTimeout(debounceTimeout);
    }

    if (value.length < MIN_SEARCH_CHARS) {
        dropdownMenu.innerHTML = '';
        dropdownMenu.style.display = 'none';
        return;
    }

    debounceTimeout = window.setTimeout(() => {
        void (async () => {
            results = await service.search(value, locale);
            selectedIndex = 0;
            renderResults();
            updateIcons();
            dispatchSearchResults(value, results.length);
        })();
    }, 500);
}

function handleKeydown(e: KeyboardEvent): void {
    if (!results.length) return;

    switch (e.key) {
        case 'ArrowDown':
            selectedIndex = selectedIndex === null ? 0 : Math.min(results.length - 1, selectedIndex + 1);
            renderResults();
            e.preventDefault();
            break;
        case 'ArrowUp':
            selectedIndex = selectedIndex === null ? 0 : Math.max(0, selectedIndex - 1);
            renderResults();
            e.preventDefault();
            break;
        case 'Enter':
            if (selectedIndex !== null) {
                handleResultClick(results[selectedIndex], selectedIndex);
            }
            e.preventDefault();
            break;
    }
}

function dispatchSearchResults(keywords: string, resultsAmount: number): void {
    const event = new CustomEvent('searchResults', {
        detail: { keywords, resultsAmount },
    });
    root.dispatchEvent(event);
}

function dispatchSearchResultUsed(result: SearchResult, index: number): void {
    const event: SearchResultUsedEvent = {
        score: result.score,
        keyword: result.item.keyword,
        label: result.item.label,
        position: index + 1,
        search: input.value,
    };

    root.dispatchEvent(new CustomEvent('searchResultUsed', { detail: event }));
}

function initialize(): void {
    input.addEventListener('input', handleInput);
    input.addEventListener('keydown', handleKeydown);
    input.addEventListener('focus', () => {

        backdrop.style.display = 'block';
    });

    backdrop.addEventListener('click', () => {
    
        dropdownMenu.innerHTML = '';
        dropdownMenu.style.display = 'none';
        backdrop.style.display = 'none';
    });

    clearIcon.addEventListener('click', clearSearch);
    startProjectBtn.addEventListener('click', () => input.focus());
}

initialize();
